import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import progressService from './progressService';

const initialState = {
  progress: [],
  courseProgress: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: '',
};

// Get user progress for all courses
export const getUserProgress = createAsyncThunk(
  'progress/getUserProgress',
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;
      return await progressService.getUserProgress(token);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get user progress for a specific course
export const getCourseProgress = createAsyncThunk(
  'progress/getCourseProgress',
  async (courseId, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;
      return await progressService.getCourseProgress(courseId, token);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const progressSlice = createSlice({
  name: 'progress',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    },
    clearCourseProgress: (state) => {
      state.courseProgress = null;
    },
    // Update progress locally after marking a lesson as completed
    updateLessonProgress: (state, action) => {
      const { lessonId, completed } = action.payload;
      
      // Update in courseProgress if it exists
      if (state.courseProgress) {
        const lessonIndex = state.courseProgress.lessons.findIndex(
          (lesson) => lesson.lesson === lessonId
        );
        
        if (lessonIndex !== -1) {
          state.courseProgress.lessons[lessonIndex].completed = completed;
          
          // Recalculate completion percentage
          const completedLessons = state.courseProgress.lessons.filter(
            (lesson) => lesson.completed
          ).length;
          
          state.courseProgress.completionPercentage = 
            (completedLessons / state.courseProgress.lessons.length) * 100;
        }
      }
      
      // Update in overall progress if it exists
      const courseIndex = state.progress.findIndex(
        (course) => course.lessons.some((lesson) => lesson.lesson === lessonId)
      );
      
      if (courseIndex !== -1) {
        const lessonIndex = state.progress[courseIndex].lessons.findIndex(
          (lesson) => lesson.lesson === lessonId
        );
        
        if (lessonIndex !== -1) {
          state.progress[courseIndex].lessons[lessonIndex].completed = completed;
          
          // Recalculate completion percentage
          const completedLessons = state.progress[courseIndex].lessons.filter(
            (lesson) => lesson.completed
          ).length;
          
          state.progress[courseIndex].completionPercentage = 
            (completedLessons / state.progress[courseIndex].lessons.length) * 100;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get user progress cases
      .addCase(getUserProgress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserProgress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.progress = action.payload;
      })
      .addCase(getUserProgress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get course progress cases
      .addCase(getCourseProgress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCourseProgress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.courseProgress = action.payload;
      })
      .addCase(getCourseProgress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, clearCourseProgress, updateLessonProgress } = progressSlice.actions;
export default progressSlice.reducer;